/**
 * Insight.jsx — HealHub Wellness Report (Enhanced, Mobile-First)
 *
 * What's new vs old version:
 *  - HealHub branding header on report (logo, date generated, user)
 *  - ALL sections open by default — this is a report, not a UI
 *  - Mood heatmap (day-by-day grid for the month)
 *  - Mood distribution bar (% of days at each mood level)
 *  - Metric progress arcs (SVG, more visual than bar)
 *  - Sentiment full breakdown with horizontal bars
 *  - HealHub feature suggestions as proper cards
 *  - Disclaimer + crisis helpline always visible at bottom
 *  - Admin users: no plan UI shown, no limits, full access permanently
 *  - PDF captures the full branded report
 *
 * Admin detection:
 *  - Firestore users/{uid}.isAdmin === true → unlimited access, no plan banner
 *  - Set via Firebase Console or setPaidUser.js
 *
 * RAZORPAY: upgrade buttons → navigate('/pricing')
 */

import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link }           from 'react-router-dom';
import { auth }                        from '../firebase';
import { signOut }                     from 'firebase/auth';
import jsPDF                           from 'jspdf';
import html2canvas                     from 'html2canvas';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  BarChart, Bar, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { authFetch, getUserClaims } from '../utils/authFetch';
//import WellnessModal                from './WellnessModal';
import WellnessModal from '../components/WellnessModal';

/* ─── Google Fonts loader ─────────────────────────────────────── */
const loadFonts = () => {
  if (document.getElementById('hh-gfonts')) return;
  const l = document.createElement('link');
  l.id = 'hh-gfonts'; l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,400&family=DM+Sans:wght@300;400;500&display=swap';
  document.head.appendChild(l);
};

/* ─── Colours ─────────────────────────────────────────────────── */
const C = {
  ink: '#1a1612', ink2: '#2e2820', ink3: '#5a4e3e', ink4: '#8c7f6d',
  ink5: '#bfb5a6', paper: '#faf8f3', paper2: '#f3efe6', paper3: '#e8e2d6',
  accent: '#c95b3d', accentL: '#fdf2ee',
  green: '#1a6b3c', greenL: '#e6f4ec',
  blue: '#1a4f8a', blueL: '#e6eef8',
  purple: '#5a3a8a', purpleL: '#efe8f8',
  amber: '#8a5a12', amberL: '#fdf3e0',
  teal: '#0d6e6e', tealL: '#e0f5f5',
};
const scoreColor = s => s >= 8 ? '#1a6b3c' : s >= 6 ? '#0d6e6e' : s >= 4 ? '#8a5a12' : '#b71c1c';

/* ─── SVG progress arc ────────────────────────────────────────── */
const Arc = ({ value, max = 10, color, size = 72, label }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);
  const dash = pct * circ;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={C.paper3} strokeWidth={6} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(.4,0,.2,1)' }} />
        <text x={size / 2} y={size / 2 + 1} textAnchor="middle" dominantBaseline="middle"
          style={{ transform: 'rotate(90deg)', transformOrigin: `${size / 2}px ${size / 2}px` }}
          fill={C.ink} fontSize={16} fontWeight={700} fontFamily="Georgia,serif">{value}</text>
      </svg>
      <div style={{ fontSize: 10, color: C.ink4, fontWeight: 500, textAlign: 'center', maxWidth: 64 }}>{label}</div>
    </div>
  );
};

/* ─── Mood heatmap ────────────────────────────────────────────── */
const MOOD_HEAT = {
  excited: '#ff9800', happy: '#4caf50', grateful: '#66bb6a', proud: '#ab47bc',
  hopeful: '#26a69a', calm: '#42a5f5', neutral: '#bfb5a6',
  tired: '#a1887f', lonely: '#64748b', sad: '#7e57c2',
  anxious: '#ef5350', overwhelmed: '#e91e63', angry: '#d32f2f', crisis: '#2196f3',
};
const MoodHeatmap = ({ trendData, month }) => {
  if (!trendData || trendData.length === 0) return null;
  const [y, m] = (month || '').split('-').map(Number);
  const total = y && m ? new Date(Date.UTC(y, m, 0)).getUTCDate() : 31;
  const byDay = {};
  trendData.forEach(d => {
    const day = parseInt(d.date?.split('-')[2] || '0');
    if (day) byDay[day] = { score: d.moodScore, mood: d.dominantMood };
  });
  const scoreToColor = s => {
    if (s >= 8) return '#1a6b3c20';
    if (s >= 6) return '#26a69a20';
    if (s >= 4) return '#ff980020';
    return '#ef535020';
  };
  const scoreToBorder = s => {
    if (s >= 8) return '#1a6b3c';
    if (s >= 6) return '#26a69a';
    if (s >= 4) return '#ff9800';
    return '#ef5350';
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
      {['S','M','T','W','T','F','S'].map((d,i) => (
        <div key={i} style={{ textAlign: 'center', fontSize: 9, color: C.ink5, fontWeight: 500, paddingBottom: 2 }}>{d}</div>
      ))}
      {Array.from({ length: total }, (_, i) => i + 1).map(day => {
        const data = byDay[day];
        return (
          <div key={day} style={{
            aspectRatio: '1', borderRadius: 6, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 10, fontWeight: 500,
            background: data ? scoreToColor(data.score) : C.paper2,
            border: `1px solid ${data ? scoreToBorder(data.score) : C.paper3}`,
            color: C.ink3,
          }}>
            {day}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Mood distribution bar ───────────────────────────────────── */
const MoodDistBar = ({ trendData }) => {
  if (!trendData || trendData.length === 0) return null;
  const zones = [
    { label: 'Great (8-10)', color: '#1a6b3c', range: [8, 10] },
    { label: 'Good (6-7)',   color: '#26a69a', range: [6, 7.9] },
    { label: 'Okay (4-5)',   color: '#ff9800', range: [4, 5.9] },
    { label: 'Low (0-3)',    color: '#ef5350', range: [0, 3.9] },
  ];
  const total = trendData.length;
  const counts = zones.map(z => ({
    ...z,
    count: trendData.filter(d => d.moodScore >= z.range[0] && d.moodScore <= z.range[1]).length,
    pct: Math.round((trendData.filter(d => d.moodScore >= z.range[0] && d.moodScore <= z.range[1]).length / total) * 100),
  }));
  return (
    <div>
      <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
        {counts.map(z => z.pct > 0 && (
          <div key={z.label} style={{ width: `${z.pct}%`, background: z.color, transition: 'width 1s' }} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px' }}>
        {counts.map(z => (
          <div key={z.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, flexShrink: 0 }} />
            <div style={{ flex: 1, color: C.ink3 }}>{z.label}</div>
            <div style={{ fontWeight: 500, color: C.ink }}>{z.pct}%</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Blurred teaser ──────────────────────────────────────────── */
const ReportTeaser = ({ onGenerate, onUpgrade }) => (
  <div style={{ position: 'relative', marginTop: 8 }}>
    <div style={{ filter: 'blur(5px)', pointerEvents: 'none', userSelect: 'none', padding: '0 16px' }}>
      <div style={{ borderRadius: 20, padding: 22, background: C.ink, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginBottom: 6 }}>March 2026 · Monthly Wellness Report</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 58, fontWeight: 900, color: '#fff', lineHeight: 1 }}>7.4</div>
          <div style={{ fontSize: 18, color: 'rgba(255,255,255,.35)' }}>/10</div>
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 6 }}>Above average — you're doing better than you think.</div>
      </div>
      {['Hopefulness · 8.1', 'Stress · 4.2', 'Stability · 7.0', 'Awareness · 8.8'].map((t, i) => (
        <div key={i} style={{ borderRadius: 14, padding: 14, border: `1px solid ${C.paper3}`, background: '#fff', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: C.ink4 }}>{t.split(' · ')[0]}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, fontFamily: 'Georgia,serif' }}>{t.split(' · ')[1]}</div>
        </div>
      ))}
    </div>
    <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, transparent 0%, ${C.paper}bb 35%, ${C.paper}f8 60%)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 24 }}>
      <div style={{ background: '#fff', borderRadius: 20, border: `1px solid ${C.paper3}`, padding: '22px 20px', margin: '0 16px', width: 'calc(100% - 32px)', textAlign: 'center' }}>
        <div style={{ fontFamily: '"Playfair Display",Georgia,serif', fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 8 }}>Your report is waiting</div>
        <div style={{ fontSize: 13, color: C.ink4, lineHeight: '1.65', marginBottom: 18, fontWeight: 300 }}>
          Generate your 1 free lifetime wellness report — score, patterns, strengths, mood trends and a personal message.
        </div>
        <button onClick={onGenerate} style={{ width: '100%', padding: 14, background: C.ink, color: C.paper, border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 10 }}>
          Generate my free report ✨
        </button>
        <button onClick={onUpgrade} style={{ width: '100%', padding: 11, background: 'transparent', color: C.ink4, border: `1px solid ${C.paper3}`, borderRadius: 14, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
          Upgrade for unlimited reports →
        </button>
      </div>
    </div>
  </div>
);

/* ─── HealHub branded report header ──────────────────────────── */
const ReportHeader = ({ month, reportType, weekNumber, userName }) => (
  <div style={{ margin: '0 16px 14px', borderRadius: 20, background: C.ink, overflow: 'hidden' }}>
    <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,.08)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>💖</div>
          <div>
            <div style={{ fontFamily: '"Playfair Display",Georgia,serif', fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: '-.01em' }}>HealHub</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.4)', fontWeight: 300 }}>Wellness Intelligence</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', fontWeight: 300 }}>Generated</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', fontWeight: 500 }}>
            {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,.35)', marginBottom: 4, fontWeight: 300, letterSpacing: '.06em', textTransform: 'uppercase' }}>
        {reportType === 'weekly' ? `Week ${weekNumber} of ` : 'Monthly Report · '}{month}
      </div>
      <div style={{ fontFamily: '"Playfair Display",Georgia,serif', fontSize: 22, fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>
        Your Emotional Wellness Report
      </div>
      {userName && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginTop: 6, fontWeight: 300 }}>Prepared for {userName}</div>
      )}
    </div>
    <div style={{ padding: '10px 20px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {['Powered by AI · Not medical advice', 'Private & encrypted', 'iCall: 9152987821'].map((t, i) => (
        <div key={i} style={{ fontSize: 9, color: 'rgba(255,255,255,.3)', padding: '3px 8px', borderRadius: 10, background: 'rgba(255,255,255,.06)', fontWeight: 400 }}>
          {t}
        </div>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════ */
/* MAIN COMPONENT                                                  */
/* ═══════════════════════════════════════════════════════════════ */
export default function Insight() {
  const navigate = useNavigate();

  const [isPaid,      setIsPaid]      = useState(false);
  const [isAdmin,     setIsAdmin]     = useState(false);
  const [tierLoaded,  setTierLoaded]  = useState(false);
  const [userName,    setUserName]    = useState('');
  const [theme,       setTheme]       = useState(localStorage.getItem('theme') || 'light-theme');

  const [reportData,         setReportData]         = useState(null);
  const [aiLoading,          setAiLoading]          = useState(false);
  const [reportError,        setReportError]        = useState('');
  const [reportLimitReached, setReportLimitReached] = useState(false);
  const [reportUsed,         setReportUsed]         = useState(0);
  const [showTeaser,         setShowTeaser]         = useState(false);
  const [cached,             setCached]             = useState(false);

  const [reportType,  setReportType]  = useState('monthly');
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [weekNumber,  setWeekNumber]  = useState(1);

  const [showWellness, setShowWellness] = useState(false);
  const [trendData,    setTrendData]    = useState([]);

  const reportRef = useRef(null);

  useEffect(() => {
    document.body.className = theme;
    loadFonts();
    if (!document.getElementById('hh-vault-styles')) {
      const s = document.createElement('style');
      s.id = 'hh-vault-styles';
      s.textContent = `@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}.hh-fade{animation:fadeUp .3s ease both}`;
      document.head.appendChild(s);
    }
  }, [theme]);

  useEffect(() => {
    const init = async () => {
      if (!auth.currentUser) return;
      const claims = await getUserClaims();
      setIsPaid(claims.paid);
      setIsAdmin(claims.isAdmin || false);
      setUserName(auth.currentUser?.displayName?.split(' ')[0] || '');
      setTierLoaded(true);

      // Load usage only for non-admin free users
      if (!claims.paid && !claims.isAdmin) {
        try {
          const u = await authFetch('/api/usage').then(r => r.json());
          setIsAdmin(u.isAdmin || false);
          setReportUsed(u.report?.used || 0);
          if ((u.report?.used || 0) >= 1) setReportLimitReached(true);
          else setShowTeaser(true);
        } catch { /**/ }
      }

      // Load mood trend data
      try {
        const res  = await authFetch('/api/report/mood-trend?days=30');
        const data = await res.json();
        setTrendData(data.trendData || []);
      } catch { /**/ }
    };
    init();
  }, []);

  const generateReport = async () => {
    setAiLoading(true); setReportError(''); setShowTeaser(false);
    try {
      const body = { reportType, month: reportMonth, ...(reportType === 'weekly' ? { weekNumber } : {}) };
      const res  = await authFetch('/api/report/generate', { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (res.status === 429 || res.status === 403) {
        setReportLimitReached(true);
        setReportError(data.error || 'Report limit reached.');
        return;
      }
      if (!res.ok) { setReportError(data.error || 'Failed to generate.'); return; }
      setReportData(data.report);
      setCached(data.cached || false);
      if (!isPaid && !isAdmin) setReportUsed(1);
    } catch { setReportError('Network error. Please try again.'); }
    finally { setAiLoading(false); }
  };

  /* ─── PDF download — captures full branded report ────────────── */
  const handleDownload = async () => {
    if (!reportData || !reportRef.current) return;
    const pdfDoc = new jsPDF({ unit: 'mm', format: 'a4' });
    let y = 15;
    const checkBreak = (n = 20) => { if (y + n > 280) { pdfDoc.addPage(); y = 15; } };

    // Header
    pdfDoc.setFillColor(26, 22, 18);
    pdfDoc.rect(0, 0, 210, 40, 'F');
    pdfDoc.setTextColor(255, 255, 255);
    pdfDoc.setFontSize(18); pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.text('HealHub', 15, 18);
    pdfDoc.setFontSize(9); pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setTextColor(180, 170, 160);
    pdfDoc.text('Emotional Wellness Report', 15, 25);
    pdfDoc.text(`${reportType === 'weekly' ? `Week ${weekNumber} · ` : ''}${reportMonth}`, 15, 31);
    pdfDoc.setTextColor(120, 112, 104);
    pdfDoc.text(`Generated ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`, 15, 37);

    y = 50;

    // Capture full report section as image
    try {
      const canvas = await html2canvas(reportRef.current, { scale: 1.5, useCORS: true, backgroundColor: '#faf8f3' });
      const imgW   = 180;
      const imgH   = canvas.height * (imgW / canvas.width);
      // Split into pages if needed
      const pageH  = 240;
      const totalPages = Math.ceil(imgH / pageH);
      for (let p = 0; p < totalPages; p++) {
        if (p > 0) { pdfDoc.addPage(); y = 15; }
        const srcY   = p * (canvas.height / totalPages);
        const srcH   = canvas.height / totalPages;
        const segCanvas = document.createElement('canvas');
        segCanvas.width  = canvas.width;
        segCanvas.height = srcH;
        const ctx = segCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        pdfDoc.addImage(segCanvas.toDataURL('image/png'), 'PNG', 15, y, imgW, pageH);
      }
    } catch (e) {
      // Fallback: text-only sections
      y = 50;
      [
        { title: 'Wellness Score', content: `${reportData.wellness_score}/10`, color: [26, 22, 18] },
        { title: 'Emotional Overview', content: reportData.emotional_overview, color: [52, 73, 94] },
        { title: 'Patterns Noticed', content: reportData.patterns_affecting_you, isList: true, color: [220, 20, 60] },
        { title: 'Strengths You Showed', content: reportData.strengths_you_showed, isList: true, color: [34, 139, 34] },
        { title: 'What Could Have Helped', content: reportData.what_could_have_helped, isList: true, color: [30, 144, 255] },
        { title: 'Encouragement', content: reportData.gentle_encouragement, color: [128, 0, 128] },
      ].forEach(sec => {
        checkBreak(30);
        pdfDoc.setFontSize(12); pdfDoc.setTextColor(...sec.color); pdfDoc.setFont('helvetica', 'bold');
        pdfDoc.text(sec.title, 15, y); y += 8;
        pdfDoc.setFontSize(9); pdfDoc.setFont('helvetica', 'normal'); pdfDoc.setTextColor(50, 50, 50);
        if (sec.isList) {
          (sec.content || []).forEach(item => {
            const lines = pdfDoc.splitTextToSize(`• ${item}`, 165);
            checkBreak(lines.length * 5); pdfDoc.text(lines, 20, y); y += lines.length * 5 + 2;
          });
        } else {
          const lines = pdfDoc.splitTextToSize(sec.content || '', 170);
          lines.forEach(line => { checkBreak(6); pdfDoc.text(line, 15, y); y += 5; });
        }
        y += 4;
      });
    }

    // Disclaimer footer on last page
    checkBreak(25);
    pdfDoc.setFillColor(243, 239, 230);
    pdfDoc.rect(10, y, 190, 20, 'F');
    pdfDoc.setFontSize(7); pdfDoc.setTextColor(140, 127, 109); pdfDoc.setFont('helvetica', 'italic');
    pdfDoc.text('Disclaimer: This report is generated by AI for personal wellness reflection only. It is not a medical diagnosis', 15, y + 7);
    pdfDoc.text('or substitute for professional mental health care. If you are in crisis, please call iCall: 9152987821.', 15, y + 12);
    pdfDoc.text(`© ${new Date().getFullYear()} HealHub · healhub.in`, 15, y + 18);

    pdfDoc.save(`HealHub_Wellness_Report_${reportMonth}.pdf`);
  };

  const toggleTheme = () => {
    const ts = ['light-theme', 'dark-theme', 'zen-theme'];
    const next = ts[(ts.indexOf(theme) + 1) % ts.length];
    setTheme(next); localStorage.setItem('theme', next); document.body.className = next;
  };

  const monthOptions = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    return d.toISOString().slice(0, 7);
  });

  /* ─── Radar chart data ──────────────────────────────────────── */
  const radarData = reportData ? [
    { subject: 'Hope', value: reportData.metrics?.hope || 0, fullMark: 10 },
    { subject: 'Stability', value: reportData.metrics?.emotional_stability || 0, fullMark: 10 },
    { subject: 'Awareness', value: reportData.metrics?.self_awareness || 0, fullMark: 10 },
    { subject: 'Calm', value: Math.max(0, 10 - (reportData.metrics?.stress || 5)), fullMark: 10 },
  ] : [];

  const isUnlimited = isPaid || isAdmin;

  /* ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight: '100vh', background: C.paper, fontFamily: '"DM Sans",system-ui,sans-serif', maxWidth: 480, margin: '0 auto' }}>

      {/* ── TOP NAV ───────────────────────────────────────────── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: C.paper, borderBottom: `0.5px solid ${C.paper3}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px' }}>
        <Link to="/dashboard" style={{ width: 36, height: 36, borderRadius: '50%', background: C.paper2, border: `1px solid ${C.paper3}`, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.ink3} strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        </Link>
        <div style={{ fontFamily: '"Playfair Display",Georgia,serif', fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: '-.01em' }}>Wellness Report</div>
        <button onClick={() => setShowWellness(true)} style={{ width: 36, height: 36, borderRadius: '50%', background: C.paper2, border: `1px solid ${C.paper3}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}>💡</button>
      </div>

      {/* ✅ WellnessModal — completely untouched */}
      {showWellness && <WellnessModal onClose={() => setShowWellness(false)} />}

      {/* Admin badge */}
      {isAdmin && (
        <div style={{ margin: '10px 16px 0', padding: '8px 14px', borderRadius: 12, background: '#1a1612', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#26a69a' }} />
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', fontWeight: 500 }}>Admin · Unlimited access · No plan required</div>
        </div>
      )}

      <div style={{ paddingBottom: 100 }}>

        {/* ── REPORT OPTIONS ─────────────────────────────────── */}
        <div style={{ padding: '16px 16px 0' }}>

          {isUnlimited && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <select value={reportType} onChange={e => setReportType(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: `1px solid ${C.paper3}`, background: '#fff', color: C.ink, fontSize: 13, fontFamily: 'inherit' }}>
                <option value="monthly">Monthly report</option>
                <option value="weekly">Weekly report</option>
              </select>
              <select value={reportMonth} onChange={e => setReportMonth(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 12, border: `1px solid ${C.paper3}`, background: '#fff', color: C.ink, fontSize: 13, fontFamily: 'inherit' }}>
                {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              {reportType === 'weekly' && (
                <select value={weekNumber} onChange={e => setWeekNumber(Number(e.target.value))}
                  style={{ padding: '10px 12px', borderRadius: 12, border: `1px solid ${C.paper3}`, background: '#fff', color: C.ink, fontSize: 13, fontFamily: 'inherit' }}>
                  {[1, 2, 3, 4].map(w => <option key={w} value={w}>Week {w}</option>)}
                </select>
              )}
            </div>
          )}

          {reportLimitReached && !isUnlimited && (
            <div style={{ padding: 14, borderRadius: 14, background: C.accentL, border: `1px solid ${C.accent}40`, marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#a05030', marginBottom: 10 }}>You've used your 1 free lifetime report.</div>
              <button onClick={() => navigate('/pricing')} style={{ width: '100%', padding: 11, background: C.accent, color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>
                Upgrade for unlimited reports — ₹35/month →
              </button>
            </div>
          )}

          {reportError && (
            <div style={{ padding: 12, borderRadius: 12, background: '#ffebee', color: '#b71c1c', fontSize: 13, marginBottom: 12 }}>
              {reportError}
            </div>
          )}

          <button
            onClick={generateReport}
            disabled={aiLoading || (reportLimitReached && !isUnlimited)}
            style={{
              width: '100%', padding: 14,
              background: aiLoading || (reportLimitReached && !isUnlimited) ? C.paper3 : C.ink,
              color: aiLoading || (reportLimitReached && !isUnlimited) ? C.ink5 : C.paper,
              border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 500,
              cursor: aiLoading || (reportLimitReached && !isUnlimited) ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all .18s', marginBottom: 4,
            }}
          >
            {aiLoading ? '✨ Generating your report...' : reportData ? '🔄 Regenerate report' : '✨ Generate my report'}
          </button>
          {cached && reportData && (
            <div style={{ textAlign: 'center', fontSize: 11, color: C.ink5, marginTop: 4 }}>⚡ Saved report for {reportMonth}</div>
          )}
        </div>

        {/* ── TEASER ─────────────────────────────────────────── */}
        {!reportData && !aiLoading && showTeaser && (
          <ReportTeaser
            onGenerate={() => { setShowTeaser(false); generateReport(); }}
            onUpgrade={() => navigate('/pricing')}
          />
        )}

        {/* ══════════════════════════════════════════════════════ */}
        {/* FULL REPORT                                            */}
        {/* ══════════════════════════════════════════════════════ */}
        {reportData && (
          <div ref={reportRef} className="hh-fade" style={{ marginTop: 16 }}>

            {/* Crisis */}
            {reportData.crisis_message && (
              <div style={{ margin: '0 16px 12px', padding: 16, borderRadius: 16, background: C.blueL, border: `1px solid ${C.blue}40` }}>
                <div style={{ fontSize: 13, color: C.blue, lineHeight: '1.7', marginBottom: 10 }}>{reportData.crisis_message}</div>
                <a href="tel:9152987821" style={{ display: 'block', padding: 11, background: C.blue, color: '#fff', borderRadius: 10, textDecoration: 'none', textAlign: 'center', fontSize: 13, fontWeight: 500 }}>
                  📞 iCall — 9152987821
                </a>
              </div>
            )}

            {/* ── BRANDED HEADER ────────────────────────────── */}
            <ReportHeader
              month={reportMonth}
              reportType={reportType}
              weekNumber={weekNumber}
              userName={userName}
            />

            {/* ── SCORE HERO ────────────────────────────────── */}
            <div style={{ margin: '0 16px 12px', borderRadius: 20, padding: '24px 20px', background: C.ink, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', width: 160, height: 160, borderRadius: '50%', background: 'rgba(201,91,61,.1)', top: -50, right: -30, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', width: 100, height: 100, borderRadius: '50%', background: 'rgba(26,107,60,.08)', bottom: -30, left: -20, pointerEvents: 'none' }} />
              <div style={{ fontSize: 11, fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,.4)', marginBottom: 6 }}>Overall wellness score</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 8 }}>
                <div style={{ fontFamily: '"Playfair Display",Georgia,serif', fontSize: 72, fontWeight: 900, color: '#fff', lineHeight: 1, letterSpacing: '-.03em' }}>
                  {reportData.wellness_score}
                </div>
                <div style={{ paddingBottom: 10 }}>
                  <div style={{ fontSize: 22, color: 'rgba(255,255,255,.3)' }}>/10</div>
                </div>
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.5)', marginBottom: 14, fontWeight: 300, lineHeight: 1.6, maxWidth: 280 }}>
                {reportData.mood_trend ? reportData.mood_trend.split('.')[0] + '.' : 'Your emotional landscape this period.'}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { label: `Stress ${reportData.metrics?.stress}/10`, ok: reportData.metrics?.stress <= 4 },
                  { label: `Hope ${reportData.metrics?.hope}/10`, ok: true },
                  { label: reportData.metrics?.emotional_stability >= 6 ? 'Stable' : 'Fluctuating', ok: reportData.metrics?.emotional_stability >= 6 },
                ].map((b, i) => (
                  <div key={i} style={{ padding: '5px 12px', borderRadius: 20, background: b.ok ? 'rgba(26,107,60,.25)' : 'rgba(255,255,255,.08)', fontSize: 11, color: b.ok ? '#6ee3a5' : 'rgba(255,255,255,.55)', fontWeight: 500 }}>
                    {b.label}
                  </div>
                ))}
              </div>
            </div>

            {/* ── METRIC ARCS ───────────────────────────────── */}
            <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '18px 16px', border: `1px solid ${C.paper3}`, background: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 4 }}>Key metrics</div>
              <div style={{ fontSize: 11, color: C.ink4, marginBottom: 18, fontWeight: 300 }}>Scores out of 10 · Lower stress is better</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, justifyItems: 'center' }}>
                <Arc value={reportData.metrics?.hope || 0} color="#26a69a" label="Hope" />
                <Arc value={reportData.metrics?.emotional_stability || 0} color="#42a5f5" label="Stability" />
                <Arc value={reportData.metrics?.self_awareness || 0} color="#ab47bc" label="Awareness" />
                <Arc value={reportData.metrics?.stress || 0} color="#ef5350" label="Stress" />
              </div>
            </div>

            {/* ── RADAR CHART ───────────────────────────────── */}
            {radarData.length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '16px', border: `1px solid ${C.paper3}`, background: '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 2 }}>Emotional profile</div>
                <div style={{ fontSize: 11, color: C.ink4, marginBottom: 10, fontWeight: 300 }}>Your strengths across 4 dimensions</div>
                <div style={{ position: 'relative', width: '100%', height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke={C.paper3} />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: C.ink4 }} />
                      <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                      <Radar name="You" dataKey="value" stroke={C.accent} fill={C.accent} fillOpacity={0.15} strokeWidth={2} dot={{ r: 4, fill: C.accent }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* ── MOOD TREND CHART ──────────────────────────── */}
            {trendData.length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '16px', border: `1px solid ${C.paper3}`, background: '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 2 }}>Mood trend · 30 days</div>
                <div style={{ fontSize: 11, color: C.ink4, marginBottom: 14, fontWeight: 300 }}>Daily average from diary entries and quick notes</div>
                <div style={{ position: 'relative', width: '100%', height: 160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                      <defs>
                        <linearGradient id="moodGrad2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={C.accent} stopOpacity={0.2} />
                          <stop offset="95%" stopColor={C.accent} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.paper3} vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 9, fill: C.ink5 }} tickLine={false} axisLine={false} interval={4} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 9, fill: C.ink5 }} tickLine={false} axisLine={false} ticks={[0, 5, 10]} />
                      <Tooltip contentStyle={{ background: '#fff', border: `1px solid ${C.paper3}`, borderRadius: 10, fontSize: 12 }} formatter={v => [`${v}/10`, 'Mood']} />
                      <Area type="monotone" dataKey="moodScore" stroke={C.accent} strokeWidth={2.5} fill="url(#moodGrad2)"
                        dot={{ r: 3, fill: C.accent, strokeWidth: 0 }} activeDot={{ r: 5, fill: C.accent }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 11, color: C.ink4, marginBottom: 10, fontWeight: 500 }}>Mood distribution</div>
                  <MoodDistBar trendData={trendData} />
                </div>
              </div>
            )}

            {/* ── MOOD HEATMAP ──────────────────────────────── */}
            {trendData.length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '16px', border: `1px solid ${C.paper3}`, background: '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 2 }}>Monthly mood calendar</div>
                <div style={{ fontSize: 11, color: C.ink4, marginBottom: 14, fontWeight: 300 }}>Green = high mood · Red = low mood · Grey = no data</div>
                <MoodHeatmap trendData={trendData} month={reportMonth} />
                <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                  {[['#1a6b3c', 'Great (8-10)'], ['#26a69a', 'Good (6-7)'], ['#ff9800', 'Okay (4-5)'], ['#ef5350', 'Low (0-3)']].map(([c, l]) => (
                    <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: C.ink4 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 3, background: `${c}25`, border: `1px solid ${c}` }} />
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SENTIMENT ─────────────────────────────────── */}
            {reportData.sentiment_overview && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '16px', border: `1px solid ${C.paper3}`, background: '#fff' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.ink, marginBottom: 14 }}>Emotional tone breakdown</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ position: 'relative', width: 110, height: 110, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[
                          { name: 'Positive', value: reportData.sentiment_overview.positive || 0 },
                          { name: 'Negative', value: reportData.sentiment_overview.negative || 0 },
                          { name: 'Neutral',  value: reportData.sentiment_overview.neutral || 0 },
                        ]} dataKey="value" innerRadius={34} outerRadius={50} paddingAngle={2}>
                          <Cell fill="#26a69a" /><Cell fill="#ef5350" /><Cell fill={C.paper3} />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <div style={{ fontFamily: 'Georgia,serif', fontSize: 20, fontWeight: 700, color: C.ink }}>{reportData.sentiment_overview.positive || 0}%</div>
                      <div style={{ fontSize: 9, color: C.ink4 }}>positive</div>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    {[
                      { label: 'Positive', val: reportData.sentiment_overview.positive || 0, color: '#26a69a' },
                      { label: 'Negative', val: reportData.sentiment_overview.negative || 0, color: '#ef5350' },
                      { label: 'Neutral',  val: reportData.sentiment_overview.neutral || 0,  color: C.paper3 },
                    ].map(item => (
                      <div key={item.label} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                          <span style={{ color: C.ink3 }}>{item.label}</span>
                          <span style={{ fontWeight: 500, color: C.ink }}>{item.val}%</span>
                        </div>
                        <div style={{ height: 5, background: C.paper2, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', background: item.color, width: `${item.val}%`, borderRadius: 3, transition: 'width 1s' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── EMOTIONAL OVERVIEW (full text, not accordion) ─ */}
            {reportData.emotional_overview && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '18px', border: `1px solid ${C.paper3}`, background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: C.purpleL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>💜</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>Emotional overview</div>
                </div>
                <div style={{ fontSize: 13, color: C.ink2, lineHeight: '1.8', fontWeight: 300 }}>{reportData.emotional_overview}</div>
              </div>
            )}

            {/* ── PATTERNS (full, not accordion) ───────────────── */}
            {(reportData.patterns_affecting_you || []).length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '18px', border: `1px solid #fce4ec`, background: '#fffafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🔍</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>Patterns noticed</div>
                </div>
                {reportData.patterns_affecting_you.map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderBottom: i < reportData.patterns_affecting_you.length - 1 ? `0.5px solid #fce4ec` : 'none' }}>
                    <div style={{ width: 24, height: 24, borderRadius: 8, background: '#fce4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#b71c1c', flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontSize: 13, color: C.ink2, lineHeight: '1.65', fontWeight: 300, paddingTop: 2 }}>{p}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── STRENGTHS ─────────────────────────────────── */}
            {(reportData.strengths_you_showed || []).length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '18px', border: `1px solid ${C.greenL}`, background: '#f8fdf9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: C.greenL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>💪</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>Strengths you showed</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {reportData.strengths_you_showed.map((s, i) => (
                    <div key={i} style={{ padding: '10px 12px', borderRadius: 12, background: C.greenL, border: `1px solid rgba(26,107,60,.15)` }}>
                      <div style={{ fontSize: 16, marginBottom: 4 }}>✦</div>
                      <div style={{ fontSize: 12, color: C.green, lineHeight: '1.5', fontWeight: 400 }}>{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── WHAT COULD HAVE HELPED ────────────────────── */}
            {(reportData.what_could_have_helped || []).length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '18px', border: `1px solid ${C.blueL}`, background: '#f7fbff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: C.blueL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>💡</div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>What could have helped</div>
                </div>
                {reportData.what_could_have_helped.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.blue, flexShrink: 0, marginTop: 8 }} />
                    <div style={{ fontSize: 13, color: C.ink2, lineHeight: '1.65', fontWeight: 300 }}>{s}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── HEALHUB FEATURE SUGGESTIONS ───────────────── */}
            {(reportData.healhub_suggestions || []).length > 0 && (
              <div style={{ margin: '0 16px 12px', borderRadius: 18, padding: '18px', border: `1px solid ${C.amberL}`, background: '#fffdf5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: C.amberL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>✨</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: C.ink }}>HealHub features for you</div>
                    <div style={{ fontSize: 10, color: C.ink4, fontWeight: 300 }}>Tailored to your patterns this month</div>
                  </div>
                </div>
                {reportData.healhub_suggestions.map((s, i) => (
                  <div key={i} style={{ padding: '12px 14px', borderRadius: 12, background: '#fff', border: `1px solid ${C.amberL}`, marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: C.amberL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>💖</div>
                    <div style={{ fontSize: 13, color: C.ink2, lineHeight: '1.6', fontWeight: 300 }}>{s}</div>
                  </div>
                ))}
              </div>
            )}

            {/* ── ENCOURAGEMENT ─────────────────────────────── */}
            {reportData.gentle_encouragement && (
              <div style={{ margin: '0 16px 12px', padding: '24px 20px', borderRadius: 20, background: C.purpleL, border: '1px solid rgba(90,58,138,.12)', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', right: -20, top: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(90,58,138,.06)', pointerEvents: 'none' }} />
                <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(90,58,138,.5)', marginBottom: 10 }}>
                  A note from Saksham 💙
                </div>
                <div style={{ fontFamily: '"Playfair Display",Georgia,serif', fontStyle: 'italic', fontSize: 17, lineHeight: '1.85', color: C.purple }}>
                  "{reportData.gentle_encouragement}"
                </div>
                <div style={{ marginTop: 14, fontSize: 11, color: 'rgba(90,58,138,.4)', fontWeight: 300 }}>
                  — Saksham, your HealHub wellness companion
                </div>
              </div>
            )}

            {/* ── DOWNLOAD PDF ──────────────────────────────── */}
            <div style={{ padding: '0 16px 4px' }}>
              <button onClick={handleDownload} style={{ width: '100%', padding: 14, background: 'transparent', color: C.ink, border: `1px solid ${C.paper3}`, borderRadius: 14, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download branded report (PDF)
              </button>
            </div>

            {/* ── DISCLAIMER ────────────────────────────────── */}
            <div style={{ margin: '8px 16px 4px', padding: '16px', borderRadius: 16, background: C.paper2, border: `1px solid ${C.paper3}` }}>
              <div style={{ fontSize: 10, fontWeight: 500, color: C.ink4, marginBottom: 6, letterSpacing: '.05em', textTransform: 'uppercase' }}>
                Important disclaimer
              </div>
              <div style={{ fontSize: 11, color: C.ink5, lineHeight: '1.75', fontWeight: 300 }}>
                This report is generated by AI for personal wellness reflection only. It is <strong style={{ fontWeight: 500, color: C.ink4 }}>not a medical diagnosis</strong> and is not a substitute for professional mental health care or therapy.
              </div>
              <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: C.blueL, border: `1px solid ${C.blue}25` }}>
                <div style={{ fontSize: 11, color: C.blue, lineHeight: '1.6' }}>
                  🆘 If you or someone you know is in crisis, please reach out immediately:
                  <br /><strong style={{ fontWeight: 600 }}>iCall India — 9152987821</strong> · Available Mon–Sat, 8am–10pm
                </div>
              </div>
              <div style={{ marginTop: 8, fontSize: 10, color: C.ink5, textAlign: 'center' }}>
                © {new Date().getFullYear()} HealHub · 💖🌿 Your space to heal, grow, and breathe.
              </div>
            </div>

          </div>
        )}
        {/* ── UPGRADE BANNER (free non-admin only) ──────────── */}
        {!isUnlimited && (
          <div onClick={() => navigate('/pricing')} 
            style={{ 
              margin: '16px', 
              padding: '16px 20px', 
              borderRadius: 20, 
              background: C.accent, 
              display: 'flex', 
              flexDirection: 'row',
              gap: 14, 
              alignItems: 'center', 
              cursor: 'pointer', 
              WebkitTapHighlightColor: 'transparent',
              textAlign: 'left'
            }}
            onTouchStart={e => e.currentTarget.style.transform = 'scale(.98)'}
            onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}>
            
            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✨</div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Unlock full Diary Vault
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.75)', fontWeight: 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Plus — ₹35/m · All Quick Notes & Reports
              </div>
            </div>
            
            <div style={{ fontSize: 20, color: 'rgba(255,255,255,.5)', flexShrink: 0 }}>›</div>
          </div>
        )}
        {/* 👇 WELLNESS SCORE BUTTON 👇 */}
        <div style={{ padding: "0 16px 32px", marginTop: isUnlimited ? "16px" : "0" }}>
          <button
            onClick={() => setShowWellness(true)}
            style={{
              width: "100%",
              padding: "16px",
              background: "#ffffff",
              color: C.ink,
              border: `1.5px solid ${C.paper3}`,
              borderRadius: 20, // Matched the banner's curve
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
              fontFamily: "inherit",
              transition: "transform 0.1s"
            }}
            onTouchStart={e => e.currentTarget.style.transform = 'scale(.98)'}
            onTouchEnd={e => e.currentTarget.style.transform = 'scale(1)'}
          >
            <span style={{ fontSize: 20 }}>💡</span> 
            Check Live Wellness Score
          </button>
        </div>
      </div>

          {/* ── BOTTOM NAV ───────────────────────────────────────── */}
      <div style={{ position: 'sticky', bottom: 0, background: C.paper, borderTop: `0.5px solid ${C.paper3}`, display: 'flex', zIndex: 20, paddingBottom: 'env(safe-area-inset-bottom,0px)' }}>
        {[
          { label: 'Journal', to: '/diary-vault', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg> },
          { label: 'Report', to: '/insight', active: true, icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg> },
          { label: 'Chat', to: '/chatbot', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
          { label: 'Home', to: '/dashboard', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
        ].map(t => (
          <Link key={t.label} to={t.to} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 4px', textDecoration: 'none' }}>
            <div style={{ width: 28, height: 28, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.active ? C.accentL : 'transparent', color: t.active ? C.accent : C.ink5, transition: 'all .18s' }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 500, color: t.active ? C.accent : C.ink5 }}>{t.label}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}